import { scopeProjectRef } from "@cleo/client-runtime/environment";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { LinkIcon, PlusIcon, RotateCcwIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { openCommandPalette } from "../commandPaletteBus";
import { openWorldRoomConnections, worldViewBoardsSeated } from "../neucleos/worldViewController";
import { sortScopedProjectsForSidebar } from "../components/Sidebar.logic";
import { Button } from "../components/ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "../components/ui/empty";
import { SidebarInset } from "../components/ui/sidebar";
import { useNewThreadHandler } from "../hooks/useHandleNewThread";
import { LocalEmptyView } from "../components/LocalView";
import {
  useAllEnvironmentShellsBootstrapped,
  useProjects,
  useThreadShells,
} from "../state/entities";
import { useEnvironments } from "../state/environments";
import { APP_DISPLAY_NAME } from "~/branding";
import { hasCloudPublicConfig } from "~/cloud/publicConfig";
import { cn } from "~/lib/utils";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "~/workspaceTitlebar";
import { WorldViewStateFace } from "../components/WorldViewStateFace";
import { LOCAL_WORLD_DOOR } from "../neucleos/worldSelection";
import { resolveLocalEntryView, useWorldView } from "../neucleos/worldView";
import { readLastDoor, readWorldRoom, useNeucleosWorldStore } from "../neucleos/worldStore";
import { FirstRunSignIn } from "../components/FirstRunSignIn";
import { WebHostLanding } from "../components/webHost/WebHostLanding";
import { readFirstRunChoice, shouldOfferFirstRunSignIn } from "../neucleos/firstRun";

function ChatIndexRouteView() {
  const { authGateState } = Route.useRouteContext();
  const { environments } = useEnvironments();

  // cleo-web (webHost.ts): the browser host opens on the person's World conversations, read from the World itself.
  if (authGateState.status === "web-host") {
    return <WebHostLanding />;
  }

  if (authGateState.status === "hosted-static" && environments.length === 0) {
    return <HostedStaticOnboardingState />;
  }

  return <IndexDraftLanding />;
}

/**
 * Landing on the index route drops straight into a draft thread for the most
 * recently active project, so the first screen is a prompt instead of a dead
 * end. Falls back to an add-project hero when no project exists yet.
 */
function IndexDraftLanding() {
  // The draft landing is the Local harness's front door. It keys off the
  // operating mode, not sign-in: a signed-in member who chose the Local door
  // gets it; a selected World gets its named room state instead.
  const { state: room } = useWorldView();
  const localMode = room.kind === "local";
  const projects = useProjects();
  const threads = useThreadShells();
  const bootstrapped = useAllEnvironmentShellsBootstrapped();
  const handleNewThread = useNewThreadHandler();
  const storeLoaded = useNeucleosWorldStore((state) => state.loaded);
  const signedIn = useNeucleosWorldStore((state) => state.signedIn);
  const [firstRunChoice, setFirstRunChoice] = useState(readFirstRunChoice);
  const startingRef = useRef(false);
  const [startState, setStartState] = useState({ failed: false, retryRequest: 0 });

  const mostRecentProject = useMemo(
    () =>
      bootstrapped
        ? (sortScopedProjectsForSidebar(projects, threads, "updated_at")[0] ?? null)
        : null,
    [bootstrapped, projects, threads],
  );

  const navigate = useNavigate();
  useEffect(() => {
    if (!localMode || !bootstrapped || startingRef.current) {
      return;
    }
    // Local restores its last live ungoverned session before minting a fresh
    // draft (WS9); a stale, archived, or World-owned memory falls through to
    // the draft landing.
    const restored = resolveLocalEntryView({
      shells: threads,
      remembered: readWorldRoom(LOCAL_WORLD_DOOR),
    });
    if (restored !== null) {
      startingRef.current = true;
      void navigate({
        to: "/$environmentId/$threadId",
        params: { environmentId: restored.environmentId, threadId: restored.threadId },
        replace: true,
      } as never);
      return;
    }
    if (mostRecentProject === null) {
      // The empty entry is the completed landing. Add project owns the
      // conversation it opens after creating the folder; reacting to that
      // project's arrival here races the action and can create a second
      // thread once its first draft has already been promoted.
      startingRef.current = true;
      return;
    }
    startingRef.current = true;
    void handleNewThread(scopeProjectRef(mostRecentProject.environmentId, mostRecentProject.id), {
      replace: true,
    }).catch(() => {
      startingRef.current = false;
      setStartState((state) => ({ ...state, failed: true }));
    });
  }, [
    bootstrapped,
    handleNewThread,
    localMode,
    mostRecentProject,
    navigate,
    threads,
    startState.retryRequest,
  ]);

  if (!localMode) {
    // A governed World never lands on a local draft; the room provider is
    // reading (or refusing, or redirecting to) the authoritative Session.
    return (
      <SidebarInset className="h-full min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
        <WorldViewStateFace />
      </SidebarInset>
    );
  }
  if (!bootstrapped) {
    return null;
  }
  if (mostRecentProject !== null) {
    return startState.failed ? (
      <DraftStartError
        onRetry={() => {
          setStartState((state) => ({
            failed: false,
            retryRequest: state.retryRequest + 1,
          }));
        }}
      />
    ) : null;
  }
  // Hold the landing until the sign-in state is read, so a new person never
  // sees the Local entry flash before the first-run choice.
  if (!storeLoaded && firstRunChoice === null && threads.length === 0) return null;
  if (
    shouldOfferFirstRunSignIn({
      storeLoaded,
      signedIn,
      choice: firstRunChoice,
      lastDoor: readLastDoor(),
      projectCount: projects.length,
      threadCount: threads.length,
    })
  ) {
    return (
      <SidebarInset className="h-full min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
        <FirstRunSignIn onChooseLocal={() => setFirstRunChoice("local")} />
      </SidebarInset>
    );
  }
  return <LocalEmptyLanding />;
}

function DraftStartError({ onRetry }: { readonly onRetry: () => void }) {
  return (
    <SidebarInset className="h-full min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <Empty className="flex-1">
        <EmptyHeader className="max-w-md">
          <EmptyTitle className="text-foreground text-xl">Couldn’t start a new thread</EmptyTitle>
          <EmptyDescription className="mt-2 text-sm text-muted-foreground/78">
            The project is still available. Try opening the draft again.
          </EmptyDescription>
          <div className="mt-5 flex justify-center">
            <Button size="sm" onClick={onRetry}>
              <RotateCcwIcon className="size-4" />
              Try again
            </Button>
          </div>
        </EmptyHeader>
      </Empty>
    </SidebarInset>
  );
}

/**
 * The retired hero's seat, now the room itself: Local with nothing to seat
 * renders the SAME session view grammar, unbound — the Cleo Panel offers
 * Add project and the Canvas names itself instead of rendering air
 * (principal directive 2026-08-28: the room is the front door in every state).
 */
function LocalEmptyLanding() {
  const openAddProject = useCallback(() => openCommandPalette({ open: "add-project" }), []);

  return (
    <SidebarInset className="h-full min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <LocalEmptyView onAddProject={openAddProject} />
    </SidebarInset>
  );
}

export const Route = createFileRoute("/_chat/")({
  component: ChatIndexRouteView,
});

function HostedStaticOnboardingState() {
  const cloudEnabled = hasCloudPublicConfig();

  return (
    <SidebarInset className="h-full min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-background">
        <header
          className={cn(
            "flex h-[var(--workspace-topbar-height)] min-h-[var(--workspace-topbar-height)] shrink-0 items-center border-b border-border px-3 transition-[padding-left] duration-200 ease-linear motion-reduce:transition-none sm:px-5",
            COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
          )}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground md:text-muted-foreground/60">
              {APP_DISPLAY_NAME}
            </span>
          </div>
        </header>

        <Empty className="flex-1">
          <div className="w-full max-w-xl rounded-3xl border border-border/55 bg-card/20 px-8 py-12 shadow-sm/5">
            <EmptyHeader className="max-w-none">
              <div className="mx-auto mb-5 flex size-11 items-center justify-center rounded-xl border border-border/70 bg-background/70 text-muted-foreground">
                <LinkIcon className="size-5" />
              </div>
              <EmptyTitle className="text-foreground text-xl">
                Connect an environment to get started
              </EmptyTitle>
              <EmptyDescription className="mt-2 text-sm leading-relaxed text-muted-foreground/78">
                {cloudEnabled
                  ? "Sign in to Cleo Connect to connect a linked environment through its managed tunnel, or add a reachable backend manually."
                  : "Add a reachable backend manually to start working from this browser."}
              </EmptyDescription>
              <div className="mt-6 flex justify-center">
                <Button
                  render={
                    <Link
                      to="/settings/connections"
                      onClick={(event) => {
                        // A seated governed room hosts Connections as the
                        // Settings Canvas tab; routed navigation is only the
                        // no-room fallback.
                        if (worldViewBoardsSeated()) {
                          event.preventDefault();
                          openWorldRoomConnections();
                        }
                      }}
                    />
                  }
                  size="sm"
                >
                  <PlusIcon className="size-4" />
                  {cloudEnabled ? "Open Connections" : "Add environment"}
                </Button>
              </div>
            </EmptyHeader>
          </div>
        </Empty>
      </div>
    </SidebarInset>
  );
}
